import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Phone, ShieldCheck } from "lucide-react";
import * as Icons from "lucide-react";
import { companyInfo } from "../data";
import { fetchCollection, fetchPageBySlug } from "../lib/cms";

// Dynamisk ikon-komponent
function ServiceIcon({ name, className }) {
  const IconComponent = Icons[name] || Icons.Wrench;
  return <IconComponent className={className} />;
}

export default function Services() {
  const [pageData, setPageData] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [page, allServices] = await Promise.all([
          fetchPageBySlug("service-og-reparation"),
          fetchCollection("services")
        ]);
        setPageData(page);
        setServices(allServices);
      } catch (error) {
        console.error("Fejl ved indlæsning af ydelser data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  return (
    <div className="bg-slate-50 text-slate-900 antialiased min-h-screen">
      {/* Header Banner */}
      <section className="relative overflow-hidden bg-slate-900 py-24 border-b border-slate-800">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/fc4842d9-cd44-468b-8ee2-6c06e8104b74.jpg"
            alt="Service & Reparation"
            className="h-full w-full object-cover filter brightness-30 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-left">
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-4xl md:text-5xl text-white">
            {pageData?.title || "Service & Reparation"}
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-slate-300">
            Vi tilbyder alt inden for service og reparation af din bil. Se vores professionelle ydelser nedenfor.
          </p>
        </div>
      </section>

      {/* Intro & Services Grid Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Intro Text */}
          <div className="mx-auto max-w-3xl text-center space-y-6 mb-16">
            <div
              className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-6 text-left md:text-center"
              dangerouslySetInnerHTML={{
                __html: pageData?.content || `
                  <p>Vi tilbyder en bred vifte af ydelser på vores moderne autoværksted i Skærbæk. Uanset om din bil skal have foretaget et serviceeftersyn, klargøring til syn, fejlsøgning på elektroniske komponenter, dækskifte, dækopbevaring, udbedring af skader på pladeværkstedet eller olieskift/skylning af automatgearkasse, står vores erfarne mekanikere klar til at hjælpe dig.</p>
                  <p>Vi følger altid bilfabrikantens forskrifter, så du beholder fabriksgarantien, og vi anvender udelukkende reservedele af original kvalitet.</p>
                `
              }}
            />
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {loading ? (
              // Skelet-loading
              [...Array(9)].map((_, i) => (
                <div key={i} className="h-64 rounded-xl border border-slate-200 bg-slate-100 animate-pulse" />
              ))
            ) : (
              services.map((service) => (
                <Link
                  key={service.id}
                  to={`/vaerkstedet/${service.slug_id}`}
                  className="group relative flex flex-col justify-between rounded-xl border border-slate-200 bg-slate-50 p-6 md:p-8 transition-all hover:border-blue-500/30 hover:bg-white hover:shadow-lg text-left"
                >
                  <div>
                    {/* Icon */}
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white text-blue-500 border border-slate-200 group-hover:border-blue-500/20 transition-colors shadow-sm">
                      <ServiceIcon name={service.icon} className="h-6 w-6" />
                    </div>

                    <h3 className="text-base font-bold text-slate-900 mt-6 group-hover:text-blue-600 transition-colors">
                      {service.title}
                    </h3>

                    <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                      {service.short_description}
                    </p>
                  </div>

                  <div className="flex items-center gap-1.5 text-xs font-bold text-blue-600 mt-6 group-hover:gap-2.5 transition-all">
                    Læs mere & bestil tid
                    <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="bg-slate-100 border-y border-slate-200 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-left">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-500/10 text-blue-600">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-sm sm:text-base font-bold text-slate-900">Bevar din fabriksgaranti hos os</h4>
              <p className="text-xs text-slate-600 mt-0.5">Vi følger altid bilfabrikantens forskrifter og stempler din servicebog.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/online-booking"
              className="flex items-center gap-2 rounded-md bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 transition-all"
            >
              <Calendar className="h-4 w-4" />
              Book tid online
            </Link>
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-900 hover:bg-slate-50 transition-all shadow-sm"
            >
              <Phone className="h-4 w-4 text-blue-500" />
              Ring {companyInfo.phoneFormatted}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
